// Detects the player failures that never throw — playback that never starts and
// playback that freezes and never recovers. It watches for the ABSENCE of the
// expected media events within a deadline (a "watchdog" timer) rather than
// waiting for an error that never comes. Decoupled from React and Shaka so it is
// unit-testable with fake timers.

export type PlaybackFailureType =
  | "playback_failed_to_start"
  | "playback_stalled_unrecovered";

// `load()` resolved (manifest + first segment fetched) but no `canplay` within
// this delay => the media never became playable (black screen).
export const START_TIMEOUT_MS = 10_000;

// Frozen mid-playback (`waiting` with no `playing`) for this long => stuck for
// good. On MSE a healthy rebuffer waits for Shaka's 2 s goal; on native HLS
// Shaka sets no goal and WebKit buffers on its own terms. 8 s clears both.
export const STALL_TIMEOUT_MS = 8_000;

interface PlaybackWatchdogOptions {
  onFailure: (type: PlaybackFailureType) => void;
  // Called when a stall recovers on its own — a breadcrumb, not an alert.
  onStallRecovered?: () => void;
  startTimeoutMs?: number;
  stallTimeoutMs?: number;
}

export interface PlaybackWatchdog {
  onLoadResolved: () => void;
  onCanPlay: () => void;
  onWaiting: () => void;
  onPlaying: () => void;
  onStopped: () => void;
  dispose: () => void;
}

export const createPlaybackWatchdog = ({
  onFailure,
  onStallRecovered,
  startTimeoutMs = START_TIMEOUT_MS,
  stallTimeoutMs = STALL_TIMEOUT_MS,
}: PlaybackWatchdogOptions): PlaybackWatchdog => {
  let startTimer: ReturnType<typeof setTimeout> | null = null;
  let stallTimer: ReturnType<typeof setTimeout> | null = null;
  let canPlaySeen = false;
  let startReported = false;
  let stallReported = false;
  let disposed = false;

  const clearStartTimer = () => {
    if (startTimer !== null) {
      clearTimeout(startTimer);
      startTimer = null;
    }
  };
  const clearStallTimer = () => {
    if (stallTimer !== null) {
      clearTimeout(stallTimer);
      stallTimer = null;
    }
  };

  return {
    onLoadResolved() {
      if (disposed || canPlaySeen || startReported || startTimer !== null)
        return;
      startTimer = setTimeout(() => {
        startTimer = null;
        if (canPlaySeen) return;
        startReported = true;
        onFailure("playback_failed_to_start");
      }, startTimeoutMs);
    },

    onCanPlay() {
      if (disposed) return;
      canPlaySeen = true;
      clearStartTimer();
    },

    onWaiting() {
      // Only a stall once playback has actually started — `waiting` before the
      // first canplay is just initial buffering.
      if (disposed || !canPlaySeen || stallReported || stallTimer !== null)
        return;
      stallTimer = setTimeout(() => {
        stallTimer = null;
        stallReported = true;
        onFailure("playback_stalled_unrecovered");
      }, stallTimeoutMs);
    },

    onPlaying() {
      if (disposed) return;
      if (stallTimer !== null) {
        clearStallTimer();
        onStallRecovered?.();
      }
    },

    onStopped() {
      if (disposed) return;
      clearStallTimer();
    },

    dispose() {
      disposed = true;
      clearStartTimer();
      clearStallTimer();
    },
  };
};
